/**
 * The Living Blueprint — Interactive slideshow.
 * 10 slides · left/right arrows · keyboard nav · progress bar.
 * URL: /blueprint
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, ChevronRight, Home, Grid3x3, Download,
  Leaf, AlertTriangle, Sprout, Cpu, Wheat, Sun, Users,
  GraduationCap, Building2, Globe, ArrowRight, Maximize2, Minimize2,
} from "lucide-react";
import { Navbar } from "@/components/hx/Navbar";

const NEON = "#0A9B5F";
const CYAN = "#06B6D4";
const SOLAR = "#F59E0B";

// ============================================================================
//  SLIDES DATA
// ============================================================================
const SLIDES = [
  {
    n: 1, kicker: "Visão", accent: NEON, icon: Leaf,
    title: "O Futuro do Nexo Água–Energia–Alimento",
    subtitle: "Hanno Nexus & BioPintoSTP",
    body: "Transformando desafios em soberania alimentar e energética através da inteligência artificial.",
    image: "/hanno/biopinto/biopinto-hero.png",
  },
  {
    n: 2, kicker: "Contexto", accent: SOLAR, icon: AlertTriangle,
    title: "A Urgência Sistémica em São Tomé e Príncipe",
    bigStat: { value: "95%", label: "de dependência de combustíveis fósseis importados para energia" },
    bullets: [
      "Insegurança alimentar aguda e produtividade agrícola rudimentar.",
      "Gestão hídrica manual, altamente ineficiente e vulnerável às secas.",
      "Custo de energia insustentável (US$ 0,22/kWh) atrelado a apagões frequentes.",
    ],
  },
  {
    n: 3, kicker: "Solução", accent: NEON, icon: Sprout,
    title: "O Centro Integrado de Agricultura Sustentável",
    body: "Uma Quinta Modelo operando com resiliência climática e eficiência de ponta, fundindo produção física (BioPintoSTP) com inteligência digital (Hanno Nexus).",
    pipeline: ["HN Energy", "HN Agriculture", "HN AI", "Parceiros Estratégicos", "BioPintoSTP · Centro Integrado"],
    image: "/hanno/biopinto/biopinto-sector-horticolas.png",
  },
  {
    n: 4, kicker: "Plataforma", accent: CYAN, icon: Cpu,
    title: 'O "Cérebro" da Operação: Plataforma Hanno Nexus (HN AI)',
    layers: [
      { name: "Camada de Interface", desc: "Dashboards executivos + decisão em tempo real", color: CYAN },
      { name: "Camada de Processamento", desc: "IA · Machine learning · integração satélite CropWatch", color: NEON },
      { name: "Camada Física", desc: "128 sensores IoT no solo · telemetria contínua", color: SOLAR },
    ],
    bullets: [
      "Previsão de colheitas e alertas climáticos para decisões precisas.",
      "Monitorização por IA e integração de dados de satélite (CropWatch).",
    ],
  },
  {
    n: 5, kicker: "Produção", accent: SOLAR, icon: Wheat,
    title: "Matriz de Produção: Abundância Diversificada e Segura",
    grid: [
      { name: "Avicultura", desc: "Segurança proteica local · fluxo de caixa constante", img: "/hanno/biopinto/biopinto-sector-avicultura.png" },
      { name: "Viveiro de Plantas", desc: "Produção basal · regeneração de ecossistemas", img: "/hanno/biopinto/biopinto-sector-viveiro.png" },
      { name: "Flores Ornamentais", desc: "Exportação · margens elevadas", img: "/hanno/biopinto/biopinto-sector-flores.png" },
      { name: "Hortícolas & Fruta", desc: "Alto valor · irrigação inteligente", img: "/hanno/biopinto/biopinto-sector-horticolas.png" },
    ],
  },
  {
    n: 6, kicker: "Infraestrutura", accent: SOLAR, icon: Sun,
    title: "O Loop Perfeito de Energia e Água",
    loop: ["Huawei FusionSolar", "Bombagem Solar DIFFUL", "Irrigação Haishun"],
    bullets: [
      "Fluxo de energia limpa (zero diesel) garantindo estabilidade ininterrupta.",
      "Economia de até 30 % de água por ajuste automatizado da rega.",
    ],
    image: "/hanno/biopinto/biopinto-loop-energy-water.png",
  },
  {
    n: 7, kicker: "Parceiros", accent: CYAN, icon: Users,
    title: "Ecossistema de Parceiros: Credibilidade e Alcance Lusófono",
    partners: [
      { name: "Huawei", role: "Estabilidade em infraestrutura solar avançada (FusionSolar).", color: SOLAR },
      { name: "Haishun", role: "Especialistas em otimização e gestão hídrica automatizada.", color: CYAN },
    ],
  },
  {
    n: 8, kicker: "Impacto Humano", accent: NEON, icon: GraduationCap,
    title: "Formação e Impacto Social",
    steps: [
      { level: "Fundações Operacionais", desc: "Alfabetização digital agrícola" },
      { level: "Especialização Tecnológica", desc: "Solar · IoT · sistemas de irrigação" },
      { level: "Visão de Negócio", desc: "Empreendedorismo local certificado" },
    ],
    body: "Dias de Campo · treino prático de alta eficiência e baixo custo para agricultores e escolas locais.",
    image: "/hanno/biopinto/biopinto-dias-de-campo.png",
  },
  {
    n: 9, kicker: "Modelo de Negócio", accent: CYAN, icon: Building2,
    title: "Um Ecossistema, Múltiplos Clientes",
    matrix: {
      services: ["Produção", "Tecnologia", "Formação", "Sustentabilidade"],
      clients: [
        { name: "Agricultores & Cooperativas", check: [true, true, true, false] },
        { name: "Escolas & Universidades",      check: [false, true, true, false] },
        { name: "Municípios",                    check: [true, true, true, true] },
        { name: "Empresas & ONGs",               check: [true, false, true, true] },
      ],
    },
  },
  {
    n: 10, kicker: "Escalabilidade", accent: NEON, icon: Globe,
    title: "A Escalabilidade do Nexo",
    subtitle: "Começa em STP · Transforma a África Lusófona",
    stages: ["Plataforma Hanno Nexus (IA/IoT)", "Produção BioPintoSTP", "Quinta Modelo & Formação", "Expansão de Clientes"],
    bullets: [
      "1. Redução drástica da pegada de carbono.",
      "2. Autonomia financeira absoluta para o pequeno agricultor.",
      "3. Liderança na transição digital agrícola da África Lusófona.",
    ],
    image: "/hanno/cabo-verde/cv-hero.png",
  },
];

// ============================================================================
//  MAIN COMPONENT
// ============================================================================
export default function BlueprintPage() {
  const [idx, setIdx] = React.useState(0);
  const [dir, setDir] = React.useState(1);
  const [fs, setFs] = React.useState(false);

  const go = (delta) => {
    const next = idx + delta;
    if (next < 0 || next >= SLIDES.length) return;
    setDir(delta);
    setIdx(next);
  };

  const jumpTo = (n) => {
    setDir(n > idx ? 1 : -1);
    setIdx(n);
  };

  const toggleFullscreen = React.useCallback(() => {
    setFs((prev) => {
      const next = !prev;
      try {
        if (next && !document.fullscreenElement) {
          document.documentElement.requestFullscreen?.().catch(() => {});
        } else if (!next && document.fullscreenElement) {
          document.exitFullscreen?.().catch(() => {});
        }
      } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
      else if (e.key === "Home") { jumpTo(0); }
      else if (e.key === "End")  { jumpTo(SLIDES.length - 1); }
      else if (e.key === "f" || e.key === "F") { e.preventDefault(); toggleFullscreen(); }
      else if (e.key === "Escape" && fs) { e.preventDefault(); toggleFullscreen(); }
    };
    const onFsChange = () => {
      if (!document.fullscreenElement && fs) setFs(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
    };
  });

  const current = SLIDES[idx];
  const progress = ((idx + 1) / SLIDES.length) * 100;

  return (
    <div className={`App relative bg-hx-bg text-hx-text min-h-screen ${fs ? "fs-mode" : ""}`} data-testid="page-blueprint" data-fs={fs ? "on" : "off"}>
      {!fs && <Navbar />}

      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 hx-grid-bg opacity-[0.14]" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-[0.10]" style={{ background: `radial-gradient(circle, ${current.accent} 0%, transparent 70%)` }} />
      </div>

      {/* Top bar: title + counter + progress */}
      <div className={`fixed left-0 right-0 z-30 backdrop-blur-xl border-b border-white/[0.10] transition-all ${fs ? "top-0" : "top-16"}`} style={{ background: "rgba(10,15,26,0.90)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="text-[9px] uppercase tracking-[0.28em] font-semibold shrink-0" style={{ color: current.accent }}>
              The Living Blueprint
            </div>
            <div className="hidden md:block text-[10px] uppercase tracking-[0.24em] text-hx-muted truncate">
              · {current.kicker}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="font-mono text-[11px] text-hx-muted tabular-nums" data-testid="slide-counter">
              <span style={{ color: current.accent }}>{String(idx + 1).padStart(2, "0")}</span>
              <span className="opacity-40"> / {SLIDES.length}</span>
            </div>
            <button
              onClick={toggleFullscreen}
              data-testid="fullscreen-toggle"
              aria-label={fs ? "Sair do modo apresentação" : "Modo apresentação"}
              className="text-[10px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-1.5 border border-white/[0.10] rounded px-2 py-1 hover:border-hx-neon/40"
              title={fs ? "Sair (F ou Esc)" : "Modo apresentação (F)"}
            >
              {fs ? <Minimize2 size={11} /> : <Maximize2 size={11} />}
              <span className="hidden sm:inline">{fs ? "Sair" : "Apresentação"}</span>
              <kbd className="hidden md:inline text-[8px] px-1 py-0.5 rounded bg-white/[0.03] font-mono opacity-70">F</kbd>
            </button>
            {!fs && (
              <>
                <Link to="/press-kit" className="text-[10px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-1.5" data-testid="link-press-kit">
                  <Grid3x3 size={11} /> <span className="hidden sm:inline">Press Kit</span>
                </Link>
                <Link to="/" className="text-[10px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-1.5" data-testid="link-home">
                  <Home size={11} /> <span className="hidden sm:inline">Home</span>
                </Link>
              </>
            )}
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-0.5 w-full bg-white/[0.03]">
          <motion.div
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="h-full"
            style={{ background: current.accent, boxShadow: `0 0 10px ${current.accent}` }}
          />
        </div>
      </div>

      {/* Slide area */}
      <main className={`relative min-h-screen transition-all ${fs ? "pt-24 pb-20" : "pt-40 pb-24"}`}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={idx}
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`slide-${current.n}`}
              className="min-h-[60vh]"
            >
              <SlideRenderer slide={current} />
            </motion.div>
          </AnimatePresence>

          {/* Slide dots (jump) */}
          <div className="mt-14 flex items-center justify-center gap-2 flex-wrap">
            {SLIDES.map((s, i) => (
              <button
                key={i}
                onClick={() => jumpTo(i)}
                data-testid={`dot-${i}`}
                aria-label={`Slide ${i + 1}`}
                className="group relative"
              >
                <span
                  className={`block rounded-full transition-all ${i === idx ? "w-8 h-1.5" : "w-1.5 h-1.5 opacity-40 group-hover:opacity-80"}`}
                  style={{ background: i === idx ? current.accent : "#fff" }}
                />
              </button>
            ))}
          </div>
        </div>
      </main>

      {/* Left arrow */}
      <button
        onClick={() => go(-1)}
        disabled={idx === 0}
        data-testid="arrow-left"
        aria-label="Slide anterior"
        className="fixed left-3 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center transition-all disabled:opacity-20 disabled:cursor-not-allowed hover:scale-110 backdrop-blur-xl border"
        style={{ background: "rgba(10,15,26,0.90)", borderColor: `${current.accent}44`, boxShadow: idx > 0 ? `0 0 24px -6px ${current.accent}` : "none" }}
      >
        <ChevronLeft size={22} strokeWidth={2} style={{ color: current.accent }} />
      </button>

      {/* Right arrow */}
      <button
        onClick={() => go(1)}
        disabled={idx === SLIDES.length - 1}
        data-testid="arrow-right"
        aria-label="Próximo slide"
        className="fixed right-3 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center transition-all disabled:opacity-20 disabled:cursor-not-allowed hover:scale-110 backdrop-blur-xl border"
        style={{ background: "rgba(10,15,26,0.90)", borderColor: `${current.accent}44`, boxShadow: idx < SLIDES.length - 1 ? `0 0 24px -6px ${current.accent}` : "none" }}
      >
        <ChevronRight size={22} strokeWidth={2} style={{ color: current.accent }} />
      </button>

      {/* Keyboard hint */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-10 text-[9px] uppercase tracking-[0.28em] text-hx-muted/60 font-mono hidden sm:block">
        ← → · espaço · F fullscreen · Home / End
      </div>
    </div>
  );
}

// ============================================================================
//  SLIDE RENDERER — decides layout based on slide shape
// ============================================================================
function SlideRenderer({ slide }) {
  const { icon: Icon, accent } = slide;

  const kicker = (
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${accent}14`, border: `1px solid ${accent}30` }}>
        <Icon size={18} style={{ color: accent }} strokeWidth={1.7} />
      </div>
      <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: accent }}>
        Slide {String(slide.n).padStart(2, "0")} · {slide.kicker}
      </div>
    </div>
  );

  const title = (
    <>
      <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl tracking-tight leading-[1.05]">
        {slide.title}
      </h1>
      {slide.subtitle && (
        <div className="mt-3 text-lg lg:text-xl text-hx-muted font-display">{slide.subtitle}</div>
      )}
    </>
  );

  // 1 · Hero cover
  if (slide.n === 1) {
    return (
      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
        <div>
          {kicker}{title}
          <p className="mt-8 text-base lg:text-lg text-hx-muted leading-relaxed max-w-2xl">{slide.body}</p>
        </div>
        <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border" style={{ borderColor: `${accent}30`, boxShadow: `0 0 60px -20px ${accent}` }}>
          <img src={slide.image} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 40%, rgba(10,15,26,0.90) 100%)` }} />
        </div>
      </div>
    );
  }

  // 2 · Big stat + bullets
  if (slide.n === 2) {
    return (
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          {kicker}{title}
        </div>
        <div>
          <div className="hx-glass rounded-2xl p-8 lg:p-10 border" style={{ borderColor: `${accent}30`, boxShadow: `0 0 40px -12px ${accent}66` }}>
            <div className="font-display font-bold text-6xl lg:text-8xl tabular-nums" style={{ color: accent, textShadow: `0 0 30px ${accent}44` }}>
              {slide.bigStat.value}
            </div>
            <div className="mt-3 text-sm text-hx-muted leading-relaxed">{slide.bigStat.label}</div>
          </div>
          <ul className="mt-8 space-y-3">
            {slide.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
                <span className="text-sm lg:text-base text-hx-text/90">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // 3 · Solution: body + pipeline + image
  if (slide.n === 3) {
    return (
      <div>
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            {kicker}{title}
            <p className="mt-8 text-base lg:text-lg text-hx-muted leading-relaxed max-w-2xl">{slide.body}</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border" style={{ borderColor: `${accent}30` }}>
            <img src={slide.image} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 40%, rgba(10,15,26,0.90) 100%)` }} />
          </div>
        </div>
        <div className="mt-10 flex items-center gap-3 flex-wrap justify-center">
          {slide.pipeline.map((p, i) => (
            <React.Fragment key={p}>
              <div className="px-4 py-2.5 rounded-full border text-xs font-mono font-semibold" style={{ background: `${accent}10`, borderColor: `${accent}44`, color: accent }}>
                {p}
              </div>
              {i < slide.pipeline.length - 1 && <ArrowRight size={14} style={{ color: `${accent}88` }} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  // 4 · Platform layers
  if (slide.n === 4) {
    return (
      <div>
        {kicker}{title}
        <div className="mt-10 grid lg:grid-cols-[1fr_1fr] gap-8 items-center">
          <div className="space-y-3">
            {slide.layers.map((L, i) => (
              <motion.div key={L.name}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.1 }}
                className="hx-glass rounded-xl p-5 border" style={{ borderColor: `${L.color}22`, borderLeftWidth: 3, borderLeftColor: L.color }}>
                <div className="text-[10px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: L.color }}>Camada {slide.layers.length - i}</div>
                <div className="mt-1 font-display font-semibold text-lg tracking-tight">{L.name}</div>
                <div className="mt-1 text-sm text-hx-muted">{L.desc}</div>
              </motion.div>
            ))}
          </div>
          <ul className="space-y-4">
            {slide.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 hx-glass rounded-lg p-4 border" style={{ borderColor: `${accent}22` }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-[10px] font-mono font-bold" style={{ background: `${accent}14`, color: accent, border: `1px solid ${accent}44` }}>
                  {i + 1}
                </div>
                <span className="text-sm lg:text-base text-hx-text/90 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // 5 · Production grid (4 sectors)
  if (slide.n === 5) {
    return (
      <div>
        {kicker}{title}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {slide.grid.map((g, i) => (
            <motion.div key={g.name}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
              className="hx-glass rounded-xl overflow-hidden border group" style={{ borderColor: `${accent}22` }}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={g.img} alt={g.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </div>
              <div className="p-4">
                <div className="font-display font-semibold text-base">{g.name}</div>
                <div className="mt-1 text-xs text-hx-muted">{g.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // 6 · Loop
  if (slide.n === 6) {
    return (
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
        <div>
          {kicker}{title}
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            {slide.loop.map((L, i) => (
              <React.Fragment key={L}>
                <div className="px-4 py-2.5 rounded-full border text-xs font-mono font-semibold" style={{ background: `${accent}10`, borderColor: `${accent}44`, color: accent }}>
                  {L}
                </div>
                {i < slide.loop.length - 1 && <ArrowRight size={14} style={{ color: `${accent}88` }} />}
              </React.Fragment>
            ))}
          </div>
          <ul className="mt-8 space-y-3">
            {slide.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
                <span className="text-sm lg:text-base text-hx-text/90">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border" style={{ borderColor: `${accent}30` }}>
          <img src={slide.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 40%, rgba(10,15,26,0.90) 100%)` }} />
        </div>
      </div>
    );
  }

  // 7 · Partners
  if (slide.n === 7) {
    return (
      <div>
        {kicker}{title}
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {slide.partners.map((p, i) => (
            <motion.div key={p.name}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.1 }}
              className="hx-glass rounded-2xl p-7 border" style={{ borderColor: `${p.color}30` }}>
              <div className="font-display font-bold text-3xl tracking-tight" style={{ color: p.color }}>
                {p.name}
              </div>
              <div className="mt-4 text-sm text-hx-muted leading-relaxed">{p.role}</div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // 8 · Steps + image
  if (slide.n === 8) {
    return (
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
        <div>
          {kicker}{title}
          <p className="mt-8 text-base text-hx-muted leading-relaxed">{slide.body}</p>
          <div className="mt-8 space-y-3">
            {slide.steps.slice().reverse().map((s, i) => (
              <motion.div key={s.level}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.1 }}
                className="flex items-center gap-4 hx-glass rounded-xl p-4 border" style={{ borderColor: `${accent}22`, marginLeft: `${(slide.steps.length - 1 - i) * 20}px` }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono font-bold" style={{ background: `${accent}14`, color: accent, border: `1px solid ${accent}44` }}>
                  {slide.steps.length - i}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-sm">{s.level}</div>
                  <div className="text-xs text-hx-muted">{s.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border" style={{ borderColor: `${accent}30` }}>
          <img src={slide.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 40%, rgba(10,15,26,0.90) 100%)` }} />
        </div>
      </div>
    );
  }

  // 9 · Client matrix
  if (slide.n === 9) {
    return (
      <div>
        {kicker}{title}
        <div className="mt-10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.22em] text-hx-muted border-b border-white/[0.10]">
                <th className="text-left font-medium py-3 pr-3">Cliente</th>
                {slide.matrix.services.map((s) => (
                  <th key={s} className="text-center font-medium py-3 px-2">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slide.matrix.clients.map((c, i) => (
                <motion.tr key={c.name}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 + i * 0.08 }}
                  className="border-b border-white/[0.10] last:border-0">
                  <td className="py-4 pr-3 font-display font-semibold text-hx-text">{c.name}</td>
                  {c.check.map((v, j) => (
                    <td key={j} className="text-center py-4 px-2">
                      {v ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ background: `${accent}20`, border: `1px solid ${accent}44` }}>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6L5 9L10 3" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                      ) : (
                        <span className="inline-block w-1 h-1 rounded-full bg-white/15" />
                      )}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // 10 · Scale + bullets + closing
  if (slide.n === 10) {
    return (
      <div>
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            {kicker}{title}
            <ul className="mt-8 space-y-3">
              {slide.bullets.map((b, i) => (
                <motion.li key={i}
                  initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.1 }}
                  className="flex items-start gap-3 hx-glass rounded-lg p-4 border" style={{ borderColor: `${accent}22` }}>
                  <span className="text-sm lg:text-base text-hx-text/90 leading-relaxed">{b}</span>
                </motion.li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border" style={{ borderColor: `${accent}30`, boxShadow: `0 0 60px -20px ${accent}` }}>
            <img src={slide.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 40%, rgba(10,15,26,0.90) 100%)` }} />
          </div>
        </div>
        <div className="mt-10 flex items-center gap-3 flex-wrap justify-center">
          {slide.stages.map((s, i) => (
            <React.Fragment key={s}>
              <div className="px-4 py-2.5 rounded-full border text-xs font-mono font-semibold" style={{ background: `${accent}10`, borderColor: `${accent}44`, color: accent }}>
                {s}
              </div>
              {i < slide.stages.length - 1 && <ArrowRight size={14} style={{ color: `${accent}88` }} />}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-12 hx-glass-strong rounded-2xl p-8 border text-center" style={{ borderColor: `${accent}44` }}>
          <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: accent }}>Fim do Blueprint</div>
          <h3 className="mt-3 font-display font-bold text-2xl lg:text-3xl">Vamos construir juntos.</h3>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/contact" className="btn-hx-primary inline-flex items-center gap-2" data-testid="blueprint-cta-contact">
              Solicitar Reunião Executiva <ArrowRight size={14} />
            </Link>
            <Link to="/press-kit" className="text-[11px] uppercase tracking-[0.24em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-2">
              Press & Investor Kit <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
